import React, { useState } from "react";
import { useQuery } from "@apollo/client/react";
import { useParams, useNavigate, useLocation } from "react-router";
import useNotification from "src/hooks/notification";
import useMutation from "src/hooks/useMutation";
import { CREATE_PROJECT_MUTATION, CREATE_CLIENT_MUTATION, DUPLICATE_PROJECT_MUTATION } from "src/constants/graphql/index";
import { CLIENTS_AND_PROJECTS_QUERY } from "src/constants/graphql/queries";
import ClientAndProjectModal from "src/components/modals/ProjectActions/ClientAndProjectModal";
import ClientOnlyModal from "src/components/modals/ProjectActions/ClientOnlyModal";
import ProjectModal from "src/components/modals/ProjectActions/ProjectModal";
import { RECEIVING_WORKFLOW_RECEIVING_ASSISTANT } from "src/components/modals/ProjectActions/projectReceivingWorkflow";
import DuplicateProjectModal from "src/components/modals/ProjectActions/DuplicateProjectModal";
import useAuth from "src/hooks/useAuth";
import useQueryParams from "src/hooks/useQueryParams";
import { FETCH_AUTH_USER_QUERY } from "src/constants/graphql/queries";

const ProjectActionsContext = React.createContext(null);

export const ProjectActionsProvider = ({ children, onSuccessProject, onSuccessClient }) => {
  const { user, setUser } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useQueryParams();
  const [openClient, setOpenClient] = useState(false);
  const [openProject, setOpenProject] = useState(queryParams.has("addProjectModal"));
  const [openClientOnly, setOpenClientOnly] = useState(false);
  const [openDuplicateProject, setOpenDuplicateProject] = useState(false);
  const { notifySuccess } = useNotification();
  const { settings } = user.company;
  const [createProject] = useMutation(CREATE_PROJECT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: FETCH_AUTH_USER_QUERY,
      },
      {
        query: CLIENTS_AND_PROJECTS_QUERY,
      },
    ],
  });

  const [createClient] = useMutation(CREATE_CLIENT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: FETCH_AUTH_USER_QUERY,
      },
      {
        query: CLIENTS_AND_PROJECTS_QUERY,
      },
    ],
  });

  const [duplicateProject] = useMutation(DUPLICATE_PROJECT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: FETCH_AUTH_USER_QUERY,
      },
      {
        query: CLIENTS_AND_PROJECTS_QUERY,
      },
    ],
  });

  const initialValues = {
    receivingWorkflow: RECEIVING_WORKFLOW_RECEIVING_ASSISTANT,
    escalationEmailSendList: "",
    projectType: "",
  };

  const clientAndProjectInitialValues = {
    ...initialValues,
    street: "",
    street2: "",
    city: "",
    zipCode: "",
  };

  const duplicateInitialValues = {
    sourceProjectId: "",
    clientId: "",
    newProjectName: "",
  };

  React.useEffect(() => {
    setOpenProject(queryParams.has("addProjectModal"));
  }, [location.search]);

  const updateProjectModalQueryParam = (isOpen) => {
    const nextParams = new URLSearchParams(location.search);

    if (isOpen) {
      nextParams.set("addProjectModal", "true");
    } else {
      nextParams.delete("addProjectModal");
      nextParams.delete("selectModeModal");
    }

    navigate({ search: nextParams.toString() }, { replace: true });
  };

  const handleToggleClient = () => setOpenClient(!openClient);
  const handleToggleProject = () => {
    if (openProject) {
      setOpenProject(false);
      updateProjectModalQueryParam(false);
      return;
    }

    setOpenProject(true);
    updateProjectModalQueryParam(true);
  };
  const handleToggleClientOnly = () => setOpenClientOnly(!openClientOnly);
  const handleToggleDuplicateProject = () => setOpenDuplicateProject(!openDuplicateProject);
  const resetLocalModals = () => {
    setOpenClient(false);
    setOpenClientOnly(false);
    setOpenDuplicateProject(false);
  };

  const navigateToProject = async projectId => {
    const baseUrl = `/projects/${projectId}/${params.tab}`;
    const queryParams = settings.receiving_wizard ? "?info=true&tab=settings" : "";
    const url = baseUrl + queryParams;
    navigate("/loading");
    await new Promise(resolve => setTimeout(resolve, 0));
    navigate(url);
  };

  const handleSubmitClient = data => {
    return createProject({
      ...data,
      onSuccess: responseData => {
        if (responseData?.client?.id) {
          setUser({
            ...user,
            clientIds: [...(user.clientIds || []), responseData.client.id],
          });
        }

        notifySuccess(
          `${settings.filter_field1} and ${settings.filter_field2} have been successfully created!`
        );

        if (responseData?.project?.id) {
          navigateToProject(responseData.project.id);
        }

        if (data.onSuccess) {
          data.onSuccess(responseData);
        }
        if (onSuccessClient) {
          onSuccessClient();
        }
      },
    });
  };

  const handleSubmitProject = data => {
    return createProject({
      ...data,
      onSuccess: responseData => {
        notifySuccess(`${settings.filter_field2} has been successfully created!`);

        if (responseData?.project?.id) {
          navigateToProject(responseData.project.id);
        }

        if (data.onSuccess) {
          data.onSuccess(responseData);
        }
        if (onSuccessProject) {
          onSuccessProject();
        }
      },
    });
  };

  const handleSubmitClientOnly = data => {
    return createClient({
      ...data,
      onSuccess: () => {
        notifySuccess(`${settings.filter_field1} has been successfully created!`);
        if (data.onSuccess) {
          data.onSuccess();
        }
        if (onSuccessClient) {
          onSuccessClient();
        }
      },
    });
  };

  const handleSubmitDuplicateProject = data => {
    return duplicateProject({
      ...data,
      onSuccess: responseData => {
        notifySuccess(`${settings.filter_field2} has been successfully duplicated!`);

        if (responseData?.project?.id) {
          navigateToProject(responseData.project.id);
        }

        if (data.onSuccess) {
          data.onSuccess(responseData);
        }
        if (onSuccessProject) {
          onSuccessProject();
        }
      },
    });
  };

  return (
    <>
      <ProjectActionsContext.Provider
        value={{
          handleToggleClient,
          handleToggleProject,
          handleToggleClientOnly,
          handleToggleDuplicateProject,
          openClient,
          openProject,
          openClientOnly,
          openDuplicateProject,
          resetLocalModals,
        }}
      >
        {children}
      </ProjectActionsContext.Provider>
      {openClient && (
        <ClientAndProjectModal
          initialValues={clientAndProjectInitialValues}
          onClose={handleToggleClient}
          onSubmit={handleSubmitClient}
        />
      )}
      {openProject && (
        <ProjectModal
           initialValues={initialValues}
          onClose={() => {
            setOpenProject(false);
            updateProjectModalQueryParam(false);
          }}
          onSubmit={handleSubmitProject}
        />
      )}
      {openClientOnly && (
        <ClientOnlyModal
          initialValues={{ clientName: "" }}
          onClose={handleToggleClientOnly}
          onSubmit={handleSubmitClientOnly}
        />
      )}
      {openDuplicateProject && (
        <DuplicateProjectModal
          initialValues={duplicateInitialValues}
          onClose={handleToggleDuplicateProject}
          onSubmit={handleSubmitDuplicateProject}
        />
      )}
    </>
  );
};

export const useProjectActions = () => React.useContext(ProjectActionsContext);
